import type { H3Event } from 'h3'
import { invoke, set } from 'lodash-es'

export const TARGETING_KEY_COOKIE = '_targetingKey'
export const SPLIT_TEST_NAME_COOKIE = '_splittestname'
export const COHORT_COOKIE = '_cohort'

// Cohort names based on NATO phonetic alphabet
export const COHORTS = [
  'alfa',
  'bravo',
  'charlie',
  'delta',
  'echo',
  'foxtrot',
  'golf',
  'hotel',
  'india',
  'juliett',
]

/** The cohort a targeting key maps to. Both sides must derive it the same way. */
export function cohortFromTargetingKey(targetingKey: string): string {
  const sum = Array.from(targetingKey).reduce(
    (acc, ch) => acc + ch.charCodeAt(0),
    0
  )
  return COHORTS[sum % COHORTS.length]
}

export function parseCookieString(
  cookieString: string
): Record<string, string> {
  const cookies: Record<string, string> = {}
  const cookiePairs = cookieString.split(';')
  cookiePairs.forEach((pair) => {
    const [key, value] = pair.split('=').map((s) => s.trim())
    if (key && value) {
      cookies[key] = decodeURIComponent(value)
    }
  })
  return cookies
}

export function getCookie(event: H3Event, name: string): string | null {
  const cookies = parseCookieString(event.headers.get('Cookie') || '')
  return cookies[name] || null
}

export function getTargetingKey(event: H3Event): string | null {
  const targetingKey = getCookie(event, TARGETING_KEY_COOKIE)
  if (!targetingKey) {
    console.warn('Targeting key cookie not found')
  }
  return targetingKey || null
}

export function getSplitTestName(event: H3Event): string | null {
  const splitTestName = getCookie(event, SPLIT_TEST_NAME_COOKIE)
  if (!splitTestName) {
    console.warn('Split test name cookie not found')
  }
  return splitTestName || null
}

export function getCohort(event: H3Event): string | null {
  const cohort = getCookie(event, COHORT_COOKIE)
  if (!cohort) {
    console.warn('Cohort cookie not found')
  }
  return cohort || null
}

/**
 * Unwraps the OpenFeature context from the H3 event.
 * @param event The H3 event object.
 * @returns The unwrapped context as a record of key-value pairs.
 */
export function getEvaluationContext(event: H3Event): Record<string, string> {
  const context: Record<string, string> =
    event.context?.openfeature?.getContext() || {}

  const targetingKey = getTargetingKey(event)
  set(context, TARGETING_KEY_COOKIE, targetingKey || '')

  const splitTestName = getSplitTestName(event)
  set(context, SPLIT_TEST_NAME_COOKIE, splitTestName || '')

  const cohort = getCohort(event)
  set(context, COHORT_COOKIE, cohort || '')

  const record: Record<string, string> = {}
  const evaluationContextHeader = event.headers.get('x-evaluation-context')
  if (evaluationContextHeader) {
    evaluationContextHeader.split(';').forEach((pair) => {
      const [key, value] = pair.split('=').map((s) => s.trim())
      if (key && value) {
        record[key] = value
      }
    })
  }
  return {
    ...context,
    ...record,
  }
}

/**
 * Evaluates the value of a feature flag using OpenFeature.
 * @param event The H3 event object containing the OpenFeature client.
 * @param key The key of the feature flag to evaluate.
 * @param defaultValue The default value to return if the flag is not set or cannot be evaluated.
 * @returns A promise that resolves to the evaluated flag value.
 */
export async function evaluateFlagValue<T>(
  event: H3Event,
  key: string,
  defaultValue: T
): Promise<T> {
  const methods = {
    string: 'getStringValue',
    boolean: 'getBooleanValue',
    number: 'getNumberValue',
  }
  const client = event.context?.openfeature
  if (!client) {
    console.warn('OpenFeature client not found in event context')
    return defaultValue
  }

  const context = getEvaluationContext(event)
  const flagValue: T = await invoke(
    client,
    // eslint-disable-next-line
    String((methods as any)[typeof defaultValue]),
    key,
    defaultValue,
    context
  )
  return flagValue
}
